import { Injectable, Inject, Optional } from "@angular/core";
import { Router } from "@angular/router";

import { first } from "rxjs/operators";

import { AppserverInstallationCredentialsRepository } from "../credentials";
import { AktuelForsyningIndstillingerObserver } from "../forsyning-indstillinger";
import { BrugerHttpClient, EksternForsyningHttpClient } from "../http";
import { RedirectService } from "../router";
import { serialiserAppserverCredentials } from "./../credentials/appserver-credentials.util";
import { AktuelBrugersInstallationerService } from "./aktuel-brugers-installationer.service";
import { Installation } from "./model/installation.model";
import { AktuelBrugerService, Rolle } from "../bruger";
import { LOGIN_ROUTE, SERVICEPRO_TEKNIKER_LOGIN_ROUTE, TECHNICIAN_LOGIN_ROUTE } from "../util";
import { APP_SHELL_LAYOUT_OPTIONS, AppShellLayoutOptions } from "../app-shell/app-shell-layout.token";

@Injectable({ providedIn: "root" })
export class VaelgInstallationService {
    constructor(
        private readonly brugerHttp: BrugerHttpClient,
        private readonly eksternHttp: EksternForsyningHttpClient,
        private readonly indstillinger: AktuelForsyningIndstillingerObserver,
        private readonly installationCredentials: AppserverInstallationCredentialsRepository,
        private readonly redirectService: RedirectService,
        private readonly aktuelBrugersInstallationerService: AktuelBrugersInstallationerService,
        private readonly aktuelBrugerService: AktuelBrugerService,
        private readonly router: Router,
        @Optional() @Inject(APP_SHELL_LAYOUT_OPTIONS) private readonly layoutOptions: AppShellLayoutOptions | null
    ) {}

    public async vaelgFoeresteInstallation(redirect: boolean = true, forbrugerNr: number): Promise<void> {
        const installationer = await this.aktuelBrugersInstallationerService.select().pipe(first()).toPromise();
        if (installationer.length) {
            // Finder installation baseret på forbrugerNr og fallback til første installation hvis ingen match
            const matchedInstallation = installationer.find((i) => i.ejendomNr === forbrugerNr) ?? installationer[0];

            await this.vaelgInstallation(matchedInstallation, redirect);
        }
    }

    public async vaelgInstallation(installation: Installation, redirect: boolean = true): Promise<void> {
        const indstillinger = await this.indstillinger.valueChanges.pipe(first()).toPromise();
        const installationNodeId = installation.forsyningIndstillinger?.forsyningNodeId;
        const aktuelNodeId = indstillinger?.forsyningNodeId;
        // ServicePro har ofte null i AktuelForsyningIndstillingerObserver – behandl som intern.
        const erIntern =
            installationNodeId == null || aktuelNodeId == null || installationNodeId === aktuelNodeId;

        if (erIntern) {
            this.vaelgInternInstallation(installation, redirect);
        } else {
            this.vaelgEksternInstallation(installation);
        }
    }

    private vaelgInternInstallation(installation: Installation, redirect: boolean): void {
        const credentials = this.installationCredentials.hent();
        const installationCredentials = installation.credentials;
        if (installationCredentials?.forsyningNodeId != null) {
            credentials.forsyningNodeId = installationCredentials.forsyningNodeId;
        }
        if (installationCredentials?.forsyningId) {
            credentials.forsyningId = installationCredentials.forsyningId;
        }
        if (installationCredentials?.cryptId) {
            credentials.cryptId = installationCredentials.cryptId;
        }
        credentials.ejendomNr = installation.ejendomNr;
        credentials.aktivNr = installation.aktivNr;
        credentials.installationNr = installation.installationNr;
        this.installationCredentials.gem(credentials);
        if (redirect) {
            const currentPath = this.router.url.split("?")[0];
            const route = this.getRedirectRoute(currentPath);
            this.redirectService.redirect(route);
        }
    }

    private vaelgEksternInstallation(installation: Installation): void {
        const eforsyningUrl = installation.forsyningIndstillinger.eforsyningUrl;
        this.eksternHttp.authenticate(installation.credentials).subscribe((http) => {
            const { ejendomNr, installationNr, aktivNr } = installation;
            const cryptId = http.credentials.cryptId;
            const origin = serialiserAppserverCredentials(this.brugerHttp.credentials);
            // eslint-disable-next-line max-len
            const url = `${eforsyningUrl}#/silent-login/unr/${ejendomNr}/inr/${installationNr}/anr/${aktivNr}/id/${cryptId}/origin/${origin}`;
            window.location.href = url;
        });
    }

    public async hentSilentLoginUrl(installation: Installation): Promise<string> {
        const eforsyningUrl = installation.forsyningIndstillinger.eforsyningUrl;
        const http = await this.eksternHttp.authenticate(installation.credentials).pipe(first()).toPromise();
        const { ejendomNr, installationNr, aktivNr } = installation;
        const cryptId = http.credentials.cryptId;
        const origin = serialiserAppserverCredentials(this.brugerHttp.credentials);
        return `${eforsyningUrl}#/silent-login/unr/${ejendomNr}/inr/${installationNr}/anr/${aktivNr}/id/${cryptId}/origin/${origin}`;
    }

    private getRedirectRoute(route: string): string {
        const empty = /^$/;
        const slash = /^\/$/;
        const logout = /^\/logout$/;
        const login = new RegExp(`^${LOGIN_ROUTE}$`);
        const loginRedirect = new RegExp(`^${LOGIN_ROUTE}/`);
        const teknikerLogin = new RegExp(`^${TECHNICIAN_LOGIN_ROUTE}$`);
        const serviceproTeknikerLogin = new RegExp(`^${SERVICEPRO_TEKNIKER_LOGIN_ROUTE}$`);
        const serviceproTeknikerLoginRedirect = new RegExp(`^${SERVICEPRO_TEKNIKER_LOGIN_ROUTE}/`);
        const installationer = /^\/install-picker$/;
        const deeplink = /^\/deeplink\//;
        const notFound = /^\/404$/;
        const ignoredRoutes = [
            empty,
            slash,
            logout,
            login,
            loginRedirect,
            teknikerLogin,
            serviceproTeknikerLogin,
            serviceproTeknikerLoginRedirect,
            installationer,
            deeplink,
            notFound
        ];

        let erFofBruger: boolean = false;
        const bruger$ = this.aktuelBrugerService.select();
        const ignoreRoute = ignoredRoutes.some((r) => r.test(route));

        bruger$.subscribe((bruger) => {
            erFofBruger = Rolle.erFofBruger(bruger.rolle);
        });

        if (ignoreRoute) {
            if (erFofBruger) {
                if (installationer.test(route)) {
                    return this.layoutOptions?.installPickerRedirectRoute ?? "/opgaver";
                }
                return this.layoutOptions?.defaultLandingRoute ?? "/opgaver";
            } else {
                return "/mit-forbrug";
            }
        }
        return route;
    }
}
